/**
 * TodayYouFly — the last screen of the morning (Matt, 2026-10-27). Jennie and Sara spent a morning
 * modelling a plane they were not crewed on because the plan never reached them, and a Slack notice
 * alone "won't necessarily make it obvious that they shouldn't work on anything else." So once the
 * morning checks are done, the day opens on the person's planes, in priority order, and nothing else is
 * offered as work. A first-priority plane that is waiting or in holding names the second as the plane to
 * work meanwhile: the one sanctioned way to switch.
 *
 * Once a day per person (localStorage), only for someone crewed on something, never in a split pane or
 * before the other morning screens (Time, Captain's report, Triage, Operations Director Triage) have
 * cleared, so screens never stack (docs/LOCKOUTS.md). Not a lockout: one button closes it. Reads the
 * shared crew index (outline/crew.js); no fetch of its own.
 */
import { useEffect, useReducer, useState } from 'react'
import { wf, isEmbedded } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { flightsForMember, crewIndexReady, ensureCrewIndex } from '../outline/crew.js'
import { normalizeFlight, isoToday, fmtDueDate } from '../services/work.js'
import { askTower } from '../services/flights.js'
import { isSeatLoaded, hasModule } from '../services/tenancy.js'

// Crew = someone who has flown in the last 90 days (the crew index's landings). Keeps the no-plane screen
// off seats that never fly (admin, bookkeeping); the crew roster itself is tower-only to read.
function isCrew(memberId) {
  const idx = ctx.crewIndex
  return !!(idx && idx.landedByMember && (idx.landedByMember[memberId] || []).length)
}

const seenKey = (memberId) => `aj:today-you-fly:${memberId}:${isoToday()}`

export default function TodayYouFly() {
  const [, force] = useReducer(v => v + 1, 0)
  const [gates, setGates] = useState({ time: null, report: null, triage: null, focus: null })
  const [closed, setClosed] = useState(false)
  const [asked, setAsked] = useState(null)   // null | 'sending' | 'sent' | error text

  useEffect(() => {
    const on = (k) => (e) => setGates(g => ({ ...g, [k]: !!(e.detail && e.detail.blocking) }))
    const pairs = [['time-gate-blocking-changed', on('time')], ['report-gate-blocking-changed', on('report')],
      ['triage-gate-blocking-changed', on('triage')], ['focus-walk-blocking-changed', on('focus')]]
    pairs.forEach(([ev, fn]) => window.addEventListener(ev, fn))
    window.addEventListener('outline-render', force)
    ensureCrewIndex()
    return () => { pairs.forEach(([ev, fn]) => window.removeEventListener(ev, fn)); window.removeEventListener('outline-render', force) }
  }, [])

  // The Operations Director Triage only reports for whoever holds that role; everyone else never hears
  // from it, so a short wait after the other three clear stands in for its answer.
  const othersClear = gates.time === false && gates.report !== true && gates.triage === false
  const [focusWaited, setFocusWaited] = useState(false)
  useEffect(() => {
    if (!othersClear) return
    const t = setTimeout(() => setFocusWaited(true), 4000)
    return () => clearTimeout(t)
  }, [othersClear])
  const ready = othersClear && gates.focus !== true && (gates.focus === false || focusWaited)

  const memberId = wf.currentMemberId
  if (closed || isEmbedded() || !memberId || !ready || !crewIndexReady()) return null
  try { if (localStorage.getItem(seenKey(memberId))) return null } catch { /* show it */ }
  const mineAll = flightsForMember(memberId)
  const isParked = f => (f.crew || []).some(c => c.memberAirtableId === memberId && c.parked)
  const flights = mineAll.filter(f => !isParked(f))
  const parked = mineAll.filter(isParked)
  // NO PLANE (Matt, 2026-10-27): the person most likely to drift is the one with nothing to fly. Only for
  // crew (someone who has flown lately), and the tower already gets a "Where the work goes"
  // card for them; the button reaches the Operations Director at once.
  if (!flights.length) {
    if (!isSeatLoaded() || !isCrew(memberId)) return null
    const name = (ctx.currentMember && ctx.currentMember.fields && (ctx.currentMember.fields['Member Name'] || ctx.currentMember.fields['Name'])) || ''
    const finish = () => { try { localStorage.setItem(seenKey(memberId), '1') } catch { /* closes for this visit */ } setClosed(true) }
    async function ask() {
      setAsked('sending')
      try { await askTower(memberId, name); setAsked('sent') } catch (e) { setAsked(String(e && e.message || e)) }
    }
    // SAY WHY, AND DON'T ASK TWICE (Matt, 2026-11-02: "This is contradictory"). The tower already sees
    // everyone without a plane on its morning crew board, so the screen says that and the message button
    // is the extra, not the main act. A plane in holding (waiting on someone outside) frees its seat, which
    // is usually why a crew member lands here; say which one and what it waits on. The tower itself crews
    // people, so it is never told to ask the tower.
    const tower = hasModule('workload') || hasModule('admin')
    const label = f => normalizeFlight(f, memberId).label
    return (
      <div style={S.overlay} role="dialog" aria-modal="true" aria-label="Today you fly">
        <div style={S.card}>
          <div style={S.eyebrow}>Today you fly</div>
          <h2 style={S.title}>{parked.length ? (parked.length === 1 ? 'Your plane is in holding' : 'Both your planes are in holding') : "You're not on a plane right now"}</h2>
          <hr style={S.rule} />
          {parked.map(f => (
            <p key={f.id} style={S.note}>{label(f)}{f.holdingReason ? ' is waiting on ' + f.holdingReason.replace(/^waiting on\s+/i, '') : ' is waiting on someone outside the firm'}.</p>
          ))}
          {parked.length > 0 && <p style={S.lead}>{parked.length === 1 ? 'It frees your seat' : 'They free your seats'} while {parked.length === 1 ? 'it waits' : 'they wait'}, and {parked.length === 1 ? 'comes' : 'come'} back to you when the wait is over.</p>}
          {tower
            ? <p style={S.lead}>You crew the planes, so put yourself on one from the Flight Board when you're ready. Firm work is fine meanwhile.</p>
            : <p style={S.lead}>The tower sees you on this morning's crew board and will put you on a plane. Until then, firm work like admin, training and file upkeep is fine.</p>}
          {asked && asked !== 'sending' && asked !== 'sent' && <p style={{ ...S.lead, color: '#c0392b' }}>{asked}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 14 }}>
            {!tower && (asked === 'sent'
              ? <span style={{ ...S.lead, margin: 0 }}>Message sent to the tower.</span>
              : <button type="button" style={S.ghost} disabled={asked === 'sending'} onClick={ask}>{asked === 'sending' ? 'Sending…' : 'Message the tower'}</button>)}
            <button type="button" style={S.go} onClick={finish}>Got it</button>
          </div>
        </div>
      </div>
    )
  }

  const rows = flights.map(f => ({ f, n: normalizeFlight(f, memberId) })).sort((a, b) => (a.n.priority || 3) - (b.n.priority || 3))
  const held = (f) => f.holdingSince ? `In holding${f.holdingReason ? ': ' + f.holdingReason : ''}` : f.waitingSince ? 'Waiting' : null
  const first = rows[0], second = rows[1]
  const meanwhile = first && second && held(first.f) && !held(second.f)

  function done() {
    try { localStorage.setItem(seenKey(memberId), '1') } catch { /* closes for this visit */ }
    setClosed(true)
  }
  function open(n) {
    done()
    if (n.zoomType && n.entityId && ctx.zoomToEntity) {
      if (ctx.closeAllWorkspaces) ctx.closeAllWorkspaces()
      ctx.zoomToEntity(n.zoomType, n.entityId, n.projectId)
    }
  }

  return (
    <div style={S.overlay} role="dialog" aria-modal="true" aria-label="Today you fly">
      <div style={S.card}>
        <div style={S.eyebrow}>Today you fly</div>
        <h2 style={S.title}>{rows.length === 1 ? 'Your plane today' : 'Your planes today, first priority first'}</h2>
        <hr style={S.rule} />
        <div style={S.list}>
          {rows.map(({ f, n }) => {
            const others = (f.crew || []).filter(c => c.memberAirtableId !== memberId).map(c => c.name + (c.isCaptain ? ' (captain)' : ''))
            const state = held(f)
            return (
              <button key={n.id} type="button" style={S.row} onClick={() => open(n)} title="Open it">
                <span style={S.rank}>{n.priority === 1 ? '1st' : n.priority === 2 ? '2nd' : ''}</span>
                <span style={S.main}>
                  <span style={S.what}>{n.project} · {n.label}</span>
                  <span style={S.sub}>
                    {n.flightNumber}{n.isCaptain ? ' · you are captain' : ''}{others.length ? ' · with ' + others.join(', ') : ''}
                    {n.due ? ' · due ' + fmtDueDate(n.due) : ''}
                    {state ? <span style={{ color: '#b8540a' }}> · {state}</span> : null}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
        {meanwhile && <p style={S.note}>{first.n.label} is {first.f.holdingSince ? 'in holding' : 'waiting'}. Work {second.n.label} while you wait.</p>}
        <p style={S.lead}>These are your planes. For anything else, ask the tower before you start.</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {/* Got it closes the card onto whatever screen is behind it (Matt, 2026-10-28); a plane above
              still opens its place in the outline when clicked. */}
          <button type="button" style={S.go} onClick={done}>Got it</button>
        </div>
      </div>
    </div>
  )
}

const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 999990, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(4,21,24,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' },
  card: { width: '100%', maxWidth: 600, background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 16, boxShadow: '0 24px 64px rgba(4,21,24,0.4)', padding: 'clamp(22px, 3vw, 34px)' },
  eyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#1a6fa8', marginBottom: 10 },
  title: { fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(17px, 2.2vw, 21px)', fontWeight: 300, letterSpacing: '0.03em', color: 'var(--aj-text)', lineHeight: 1.3, margin: 0 },
  rule: { height: '0.5px', background: 'var(--aj-border)', border: 'none', margin: '14px 0', width: 60 },
  list: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 },
  row: { display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', width: '100%', padding: '12px 14px', borderRadius: 10, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-subtle)', cursor: 'pointer', font: 'inherit', color: 'var(--aj-text)' },
  rank: { fontFamily: '"DM Mono", monospace', fontSize: 13, fontWeight: 600, minWidth: 30, color: 'var(--aj-text)' },
  main: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  what: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600 },
  sub: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 2 },
  note: { fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, color: 'var(--aj-text)', margin: '0 0 8px', lineHeight: 1.5 },
  lead: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', margin: '0 0 16px', lineHeight: 1.5 },
  ghost: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, padding: '10px 16px', borderRadius: 8, border: '0.5px solid var(--aj-border)', background: 'none', color: 'var(--aj-text)', cursor: 'pointer' },
  go: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, padding: '10px 18px', borderRadius: 8, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
}
